import { toString } from 'lodash';

const NON_WORD_CHARACTERS = /\W+/g;
const LEADING_AND_TAILING_HYPHEN = /(^-+|-+$)/g;
const SLUG_PARAM_PATTERN = /^([\w-]*[\w]+\b)?-*(\w+)$/;

/**
 * Replaces all non-word characters with a hyphen. All letters converted to lover-case.
 * Hyphens are always single and never occur at the beginning and the end of the result string.
 *
 * @param value
 * @returns {string}
 */
export function slugify(value) {
  return toString(value)
    .replace(NON_WORD_CHARACTERS, '-')
    .replace(LEADING_AND_TAILING_HYPHEN, '')
    .toLowerCase();
}

/**
 * Creates a slug param from the name and id arguments given.
 *
 * @param name
 * @param id
 * @returns {string}
 * @throws Error if 'id' value given is not a valid identifier
 */
export function slugifyParam(name, id) {
  const idSlug = toString(id);
  if (idSlug === '') {
    throw new Error(`'${idSlug}' is not a valid slug param identifier`);
  }

  const nameSlug = slugify(name);
  return (nameSlug === '' ? idSlug : `${nameSlug}-${idSlug}`);
}

/**
 * Creates a slug param from the object given.
 * The method assumes that the object has first of the ['name', 'title', 'text'] properties
 * and 'id' property that will be used to generate the slug.
 *
 * @param object
 * @returns {string}
 */
export function slugifyObject(object) {
  const displayName = object.name || object.title || object.text;
  return slugifyParam(displayName, object.id);
}

/**
 * Creates a slug from the array elements using the separator given.
 * The array elements may be objects, in which case slugifyObject(...) method will be used. Otherwise, it's
 * assumed the elements are simple type and will be passed to slugify(...) directly.
 *
 * @param array
 * @param separator
 * @returns {string}
 */
export function slugifyArray(array, separator = ',') {
  return array
    .filter(Boolean)
    .map(elem => (typeof elem === 'object'
      ? slugifyObject(elem)
      : slugify(elem)
    ))
    .join(separator);
}

function deslugifyString(slugString) {
  const match = SLUG_PARAM_PATTERN.exec(slugString);
  if (match) {
    return match[2];
  }

  return '';
}

/**
 * Extracts param value(s) from the slug given. The input string may actually be a list of slugs, in which
 * case the list will be split using separator given, deslugified and then joined back using the separator given.
 *
 * @param slug
 * @param separator
 * @returns {String}
 * @throws Error if argument is not a valid slug
 */
export function deslugifyParam(slug, separator = ',') {
  const slugString = toString(slug);
  return slugString.indexOf(separator) > -1 // check if not array string
    ? slugString
      .split(separator)
      .map(deslugifyString)
      .filter(item => item)
      .join(separator)
    : deslugifyString(slugString);
}
